/**
 * 📅 calendarView — pure helpers for the house calendar (09-19,
 * HOUSE_CALENDAR_OUTINGS_SPEC §2.2; Nina's D2 ruling: A = the 📅 door in the
 * message box carries the AGENDA list, C = a countdown band on the room plate).
 *
 * Input is the backend's `house.calendar` = { today, rows: [{ day, kind, emoji,
 * label, by?, phase?, featured }] }, already sorted by day. Everything here is
 * plain string/date-key math so jest can pin it with no DOM.
 *
 * ⚠️ `today` is the HOUSE day the backend sent (Phoenix, one-clock). Never
 * mint a day from the viewer's own clock: a viewer in Tokyo would read
 * "tomorrow" for a night the house calls "tonight".
 * ⚠️ The band is a BAND, never a clock ("this weekend", "tomorrow",
 * "tonight"): a countdown timer reads as a promise to the minute, and the
 * engine starts an event when people are actually around.
 */

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
const noon = (day) => Date.parse(`${day}T12:00:00Z`); // noon-anchored: no tz can skew the weekday

export const BAND_DAYS = 3;   // the plate band stays hidden until a night is this close
export const AGENDA_ROWS = 8;

const rowsOf = (cal) => ((cal && Array.isArray(cal.rows) && KEY_RE.test(String(cal.today)))
  ? cal.rows.filter((r) => r && KEY_RE.test(String(r.day)) && r.day >= cal.today && r.label)
  : []);

/** Whole house days from a to b (b later = positive). */
export function dayDiff(a, b) {
  if (!KEY_RE.test(String(a)) || !KEY_RE.test(String(b))) return null;
  return Math.round((noon(b) - noon(a)) / 86400000);
}

export const weekdayOf = (day) => WEEKDAYS[new Date(noon(day)).getUTCDay()];
export const shortDate = (day) => `${MONTHS[Number(day.slice(5, 7)) - 1]} ${Number(day.slice(8, 10))}`;

/** "on now" · "today" / "tonight" · "tomorrow" · "this weekend" · "on Thursday" · null (further out). */
export function relWord(row, today, hour = 12) {
  if (!row) return null;
  if (row.phase === 'running') return 'on now';
  const diff = dayDiff(today, row.day);
  if (diff === null || diff < 0) return null;
  if (diff === 0) return hour >= 16 ? 'tonight' : 'today';
  if (diff === 1) return 'tomorrow';
  if (diff > 6) return null;
  const wd = new Date(noon(row.day)).getUTCDay();
  return (wd === 0 || wd === 6) ? 'this weekend' : `on ${WEEKDAYS[wd]}`;
}

/** The night the door and the band talk about: something running now, else
 *  the next featured night (a pinned night, a sky night, a resident's plan). */
export function nextFeatured(cal) {
  const rows = rowsOf(cal).filter((r) => r.featured);
  return rows.find((r) => r.phase === 'running') || rows[0] || null;
}

/** The door's tiny label. It IS the advert: a date, then a weekday, then
 *  "tmrw", then "today", then "now". hot = worth a glow. */
export function doorLabel(cal, now = null, tz = null) {
  const next = nextFeatured(cal);
  if (!next) return { text: '', hot: false, title: 'House calendar' };
  const diff = dayDiff(cal.today, next.day);
  const title = `House calendar. Next: ${next.label}`;
  if (next.phase === 'running') return { text: 'now', hot: true, title };
  // ⏳ 09-22: on the day, with a clock to count from, the leaf ticks (A of the countdown rulings)
  if (diff === 0 && Number.isFinite(now) && next.at && Number.isFinite(Date.parse(next.at))) {
    const t = leafClock(Date.parse(next.at), now, tz);
    if (t) return { text: t, hot: true, title };
  }
  if (diff === 0) return { text: 'today', hot: true, title };
  if (diff === 1) return { text: 'tmrw', hot: false, title };
  if (diff <= 6) return { text: weekdayOf(next.day).slice(0, 3), hot: false, title };
  return { text: shortDate(next.day), hot: false, title };
}

/** The plate band, or null while nothing is close. */
// ⚠️ 09-19 (Nina): RETIRED FROM THE PLATE. "There shouldn't be a movie night
// chip here, isn't it just supposed to be in the calendar?" GameStepPage no
// longer calls this; the agenda door is the only home for upcoming nights.
// Kept (with its tests) because the countdown phrasing is the one tested
// source if a band ever returns somewhere it belongs.
export function plateBand(cal, hour = 12) {
  const next = nextFeatured(cal);
  if (!next) return null;
  const diff = dayDiff(cal.today, next.day);
  if (next.phase !== 'running' && (diff === null || diff > BAND_DAYS)) return null;
  const rel = relWord(next, cal.today, hour);
  if (!rel) return null;
  return { text: `${next.emoji || '📅'} ${next.label} ${rel === 'on now' ? 'is on now' : rel}`, hot: next.phase === 'running' || diff === 0 };
}

/** 🍎🧺 The plate band while you are standing IN the outing: the shared basket.
 *  `outing` is the backend's house.outing = { basket, goal, yours, cap, gold_by }. */
export function outingBand(outing) {
  if (outing && outing.id === 'maze') return mazeBand(outing); // 🌽 09-28 maze: keyed on the id, lanterns never a basket
  if (!outing || !Number.isFinite(Number(outing.goal)) || Number(outing.goal) <= 0) return null;
  const basket = Math.max(0, Math.min(Number(outing.goal), Number(outing.basket) || 0));
  const full = basket >= Number(outing.goal);
  const yours = Math.max(0, Number(outing.yours) || 0);
  const mine = Number(outing.cap) > 0 ? ` · you ${Math.min(yours, Number(outing.cap))} of ${Number(outing.cap)}` : '';
  const gold = outing.gold_by ? ` · ✨ ${String(outing.gold_by).slice(0, 24)} found the golden apple` : '';
  return { text: `🧺 ${full ? 'Basket full! ' : ''}${basket} of ${Number(outing.goal)}${mine}${gold}`, hot: true };
}

/** 🍎 The plate VERBS while you are standing in the outing, or null = "this
 *  outing has nothing to tap, show the room's ordinary chips". Keys on the same
 *  signal as outingBand (the payload carries a basket), so the chip and the
 *  band can never disagree. 09-20: the plate used to offer /pick at ANY outing,
 *  which put an apple chip in the lighthouse lamp room. */
export function outingVerbs(outing) {
  // 🌽 09-28 maze: the two walking chips until you are out, then the room's ordinary chips
  if (outing && outing.id === 'maze') return (mazeBand(outing) && outing.you_out !== true) ? ['/left', '/right'] : null;
  return outingBand(outing) ? ['/pick'] : null;
}

/** 🌽🏮 09-28 maze: the plate band while you are standing at the corn maze (spec §8.5).
 *  `outing` = the backend's house.outing for the maze = { id: 'maze', lit,
 *  junctions, out, at, you_out, hint } (hint = the lantern at YOUR turn, or null).
 *  No junctions = an old or broken payload = no band. */
function mazeBand(outing) {
  const total = Math.floor(Number(outing.junctions));
  if (!Number.isFinite(total) || total <= 0) return null;
  const lit = Math.max(0, Math.min(total, Math.floor(Number(outing.lit) || 0)));
  const at = Math.max(0, Math.min(total, Math.floor(Number(outing.at) || 0)));
  const out = Math.max(0, Math.floor(Number(outing.out) || 0));
  const you = outing.you_out === true ? 'you made it out' : (at === 0 ? 'you: at the entrance' : `you: junction ${at}`);
  const hint = (outing.you_out !== true && (outing.hint === 'left' || outing.hint === 'right')) ? ` · lantern on the ${outing.hint}` : '';
  return { text: `🏮 ${lit} of ${total} lanterns · ${you}${out > 0 ? ` · ${out} out` : ''}${hint}`, hot: true };
}

/** 🎬🍿 The film ballot (backend `house.ballot`, LIGHTHOUSE_SCREENING_SPEC 09-20)
 *  as the door draws it, or null. Pure. `winner` only exists once it closed. */
// (review L1) weekdays come from the night itself, never hard-coded: a resident can schedule a screening any day
const nightOf = (ballot) => (ballot && KEY_RE.test(String(ballot.day)) ? weekdayOf(ballot.day) : 'the day');
const eveOf = (ballot) => (ballot && KEY_RE.test(String(ballot.day)) ? WEEKDAYS[new Date(noon(ballot.day) - 86400000).getUTCDay()] : 'the day before');

export function ballotView(ballot) {
  // 🎭 the GENRE stage comes first (09-20 late): same card shape, `cmd` is the
  // text the private `film` command takes ('g:<n>' for a genre, '<n>' for a film)
  if (ballot && ballot.stage === 'genre' && Array.isArray(ballot.genres) && ballot.genres.length) {
    const gTotal = Math.max(0, Number(ballot.genre_total) || 0);
    const gMine = Number.isInteger(ballot.genre_yours) ? ballot.genre_yours : null;
    return {
      open: true, stage: 'genre',
      note: `What kind of film? · ${gTotal} ${gTotal === 1 ? 'vote' : 'votes'} · the films go up ${eveOf(ballot)} at 5`,
      films: ballot.genres.slice(0, 6).map((g, i) => {
        const votes = Math.max(0, Number(g.votes) || 0);
        return {
          idx: i, cmd: `g:${i}`,
          title: `${g.emoji ? `${g.emoji} ` : ''}${String(g.label || 'A film').slice(0, 40)}`,
          meta: '', pitch: String(g.pitch || '').slice(0, 120),
          votes, pct: gTotal > 0 ? Math.round((100 * votes) / gTotal) : 0,
          mine: gMine === i, winner: false,
        };
      }),
    };
  }
  if (!ballot || !Array.isArray(ballot.films) || !ballot.films.length) return null;
  const total = Math.max(0, Number(ballot.total) || 0);
  const open = ballot.open === true;
  const yours = Number.isInteger(ballot.yours) ? ballot.yours : null;
  const n = `${total} ${total === 1 ? 'vote' : 'votes'}`;
  return {
    open, stage: open ? 'film' : 'closed',
    note: `${ballot.genre && ballot.genre.label ? `${ballot.genre.emoji ? `${ballot.genre.emoji} ` : ''}${ballot.genre.label} won · ` : ''}${open ? `Vote the film · ${n} · closes at 5 on the day` : `Voting closed · ${n}`}`,
    films: ballot.films.slice(0, 6).map((f, i) => {
      const votes = Math.max(0, Number(f.votes) || 0);
      return {
        idx: i, cmd: String(i),
        title: String(f.title || 'Untitled').slice(0, 60),
        meta: [f.year, f.runtime_min ? `${f.runtime_min} min` : null, f.silent ? 'silent' : null].filter(Boolean).join(' · '),
        pitch: String(f.pitch || '').slice(0, 120),
        votes,
        pct: total > 0 ? Math.round((100 * votes) / total) : 0,
        mine: yours === i,
        winner: !open && ballot.winner === i,
      };
    }),
  };
}

/** 🎭 The poll-looking INVITATION (Nina 09-20 late): a visitor who has not
 *  voted in the stage that is open right now gets a card, only for them. It
 *  never uses the room's poll slot. null = nothing to ask this viewer. */
export function ballotInvite(ballot) {
  const v = ballotView(ballot);
  if (!v || !v.open || v.films.some((f) => f.mine)) return null;
  return {
    id: `${ballot.event_id || ballot.day}|${v.stage}`,
    stage: v.stage,
    question: v.stage === 'genre' ? `Movie night is ${nightOf(ballot)}. What kind of film?` : `Movie night is ${nightOf(ballot)}. Which film?`,
    hint: v.stage === 'genre' ? `The films go up ${eveOf(ballot)} at 5. Change your vote any time in the 📅.` : `Voting closes ${nightOf(ballot)} at 5. Change your vote any time in the 📅.`,
    options: v.films,
  };
}

/** 🎬 The plate strip while you stand in a screening's room (backend
 *  `house.screening`): what is on and how far in. null when nothing screens.
 *  `watching` = you are already seated in the theater. */
export const WATCH_VERB = '▶ Watch with the room';
export function screeningStrip(screening, now = Date.now(), watching = false) {
  if (!screening || !screening.title || !screening.starts_at) return null;
  const t = (now - new Date(screening.starts_at).getTime()) / 1000;
  const dur = Number(screening.duration_s) || 0;
  const name = String(screening.title).slice(0, 50);
  if (t < 0) {
    const s = Math.ceil(-t);
    return { text: `🎬 ${name} · starts in ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`, verbs: watching ? null : [WATCH_VERB], hot: true };
  }
  if (dur > 0 && t >= dur) return { text: `🎬 ${name} · The End`, verbs: null, hot: false };
  return { text: `🎬 ${name} · ${Math.max(1, Math.round(t / 60))} min in`, verbs: watching ? null : [WATCH_VERB], hot: true };
}

/** 🔥 10-03 game nights: the campfire's tin as the night's row shows it, or null. `campfire` =
 *  the backend's house.campfire = { day, open, count, mine? } (ABSENT while HOUSE_CAMPFIRE_ENABLED
 *  is off, so every row stays exactly as before). Only the pinned house row on that day carries it.
 *  → { text, chip } (chip = the command the row's text button seeds, or null once the tin is closed). */
export function campfireTin(campfire, row) {
  if (!campfire || typeof campfire !== 'object' || !row || row.kind !== 'house' || !row.featured) return null;
  if (!KEY_RE.test(String(campfire.day)) || row.day !== campfire.day) return null;
  const n = Math.max(0, Math.floor(Number(campfire.count) || 0));
  // the readings started (review 10-03): the row stops counting a tin that is being read aloud
  if (campfire.open !== true) return { text: 'Tales by the fire', chip: null };
  const count = n === 0 ? 'The tin is empty. Drop the first tale.'
    : `${n} ${n === 1 ? 'tale' : 'tales'} in the tin${campfire.mine === true ? ', yours too' : ''}`;
  return { text: count, chip: '/tale' };
}

/** The agenda list behind the door.
 *  🚪 09-19 pm: with the plate band retired (Nina), a viewer sitting in the sun
 *  room had no way to FOLLOW the house out except noticing the picker glow. The
 *  running row now carries the way there: `go` = { room, label } while the house
 *  is out somewhere you are not, `here: true` once you are. `opts.outing` is the
 *  backend's house.outing (absent at home, so every other day is unchanged). */
export function agendaRows(cal, hour = 12, n = AGENDA_ROWS, opts = {}) {
  const next = nextFeatured(cal);
  const outing = (opts && opts.outing && opts.outing.id) ? opts.outing : null;
  return rowsOf(cal).slice(0, n).map((r, i) => {
    const rel = relWord(r, cal.today, hour);
    const out = (outing && r.phase === 'running' && r.featured)
      ? (opts.viewerRoom === outing.id ? { here: true } : { go: { room: outing.id, label: String(outing.label || 'there').slice(0, 40) } })
      : {};
    // 🎬🍿 09-20 the film ballot rides the row of the night it is for
    const vote = (opts && opts.ballot && r.featured && r.day === opts.ballot.day) ? ballotView(opts.ballot) : null;
    // 🔥 10-03 game nights: the campfire's tin rides its own night's row (absent house.campfire = no key)
    const tin = campfireTin(opts && opts.campfire, r);
    return {
      ...out,
      ...(vote ? { ballot: vote } : {}),
      ...(tin ? { tin } : {}),
      key: `${r.day}|${r.kind}|${i}`,
      emoji: r.emoji || '📅',
      label: r.label,
      dateLine: `${weekdayOf(r.day).slice(0, 3)}, ${shortDate(r.day)}${rel ? ` · ${rel}` : ''}`,
      by: r.by ? `${r.by} put this on the calendar` : null,
      next: r === next,
      quiet: !r.featured,
    };
  });
}

// ⏳🎬 09-22 (Nina's rulings, show day): THE COUNTDOWN. One line above the
// message box, all day on an event day, for EVERY calendar row (rent and the
// equinox included); rows with an opening instant (`at`, from the backend)
// get a ticking clock; the film's title takes over the moment the ballot
// closes; at the doors and during the film the same line is the way in.
// Pure: (calendar, { now, ballot, outing, screening }) → strip | null.
// The door leaf (A) and the strip (B) both read this; the page ticks `now`.
export const STRIP_GRACE_MS = 20 * 60 * 1000;   // "doors opening" lingers this long past the hour, then the line hides
export const STRIP_HOT_MS = 3 * 60 * 60 * 1000; // inside three hours the line and the leaf go warm

export function clockWords(ms) {
  if (!Number.isFinite(ms)) return '';
  if (ms <= 0) return 'any minute';
  const m = Math.ceil(ms / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60), mm = m % 60;
  return `in ${h}h ${String(mm).padStart(2, '0')}m`;
}

/** The leaf's clock: a compact form of the same thing ('2h14' · '42m' · '7pm').
 *  🕐 09-24 (Nina: "the time should reflect the time in the Claude house"):
 *  with `tz` the start hour reads on the HOUSE clock, so a viewer in another
 *  zone sees the same "7pm" the house does. Without it: the old local read. */
export function leafClock(atMs, now, tz = null) {
  const ms = atMs - now;
  if (!Number.isFinite(ms)) return '';
  if (ms <= 0) return 'now';
  if (ms > STRIP_HOT_MS) {
    try { return new Date(atMs).toLocaleTimeString('en-US', { hour: 'numeric', ...(tz ? { timeZone: tz } : {}) }).replace(/\s?([AP])M/i, (_, x) => x.toLowerCase() + 'm').replace(/\s+/g, ''); } catch (e) { return 'today'; }
  }
  const m = Math.ceil(ms / 60000);
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}

// ── 🕐 THE DOOR TELLS THE HOUSE'S TIME (09-24, Nina: mockup option C,
// revised, https://claude.ai/artifact/Ld2gtBtpa3sb65Z4KmMyKB) ──────────────
// The calendar door used to be an advert for the NEXT featured night: its leaf
// carried that night's date, so all week before a Saturday event it said 26
// and read as the wrong date. Now:
//   · the leaf = TODAY's house date (cal.today, the backend's house day)
//   · at rest: the house clock and a short weather word ("7:12pm · rain")
//   · a featured night tomorrow: a dot on the leaf (the agenda says which)
//   · the event's day: the countdown (the start hour, then minutes), lit
//     🧭 10-01 composer (R24 #1): the clock (lit) until 3 hours out, then the minutes
//   · while it runs: "On now", lit
// "The time should reflect the time in the Claude house": the clock and the
// start hour both read in the HOUSE zone (the payload's circadian clock.tz),
// never the viewer's. Weather is a WORD, never an emoji (the fog emoji is a
// gray box on Nina's Mac, LIVING_ROOMS handoff), and never a drawn window
// (Nina 09-24: "we don't want the window").
export const HOUSE_TZ = 'America/Phoenix'; // the ruled house zone (CIRCADIAN §2); the payload's clock.tz wins
// Short door words per weatherAlmanac KINDS (its labels are sentence prose:
// "morning fog" at 11pm, "wind and leaves"). Unknown kinds say nothing.
export const WEATHER_WORD = { rain: 'rain', storm: 'storm', snow: 'snow', fog: 'fog', heat: 'heat', leaves: 'windy', dust: 'dusty', bloom: 'pollen' };

/** "7:12pm": the instant on the house's clock, the same shape the backend's
 *  circadian clock label (and the residents' clock line) uses. */
export function houseClockLabel(now, tz = HOUSE_TZ) {
  if (!Number.isFinite(now)) return '';
  try {
    return new Date(now).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz || HOUSE_TZ })
      .replace(/\s?([AP])M/i, (_, x) => x.toLowerCase() + 'm').replace(/\s+/g, '');
  } catch (e) { return ''; }
}

/** The calendar door's whole face: { num, words, weather, hot, soon, title }. */
export function doorFace(cal, now, { tz = HOUSE_TZ, weatherKind = null } = {}) {
  const zone = tz || HOUSE_TZ;
  const today = cal && KEY_RE.test(String(cal.today)) ? cal.today : null;
  const num = today ? String(Number(today.slice(8, 10))) : '';
  const label = doorLabel(cal, now, zone);
  const clock = houseClockLabel(now, zone);
  const weather = (weatherKind && WEATHER_WORD[weatherKind]) || null;
  const said = clock ? `It's ${clock} at the house${weather ? `, ${weather}` : ''}.` : '';
  const title = [label.title, said].filter(Boolean).join('. ');
  if (label.hot) {
    // 🧭 10-01 composer (R24 #1, Nina: "clock until 3h out, then countdown, then On now"):
    // on the event's day the door keeps the HOUSE clock (still lit) while the
    // start is more than 3 hours away; the countdown takes it inside 3 hours.
    // No start time ('Today') and "On now" are unchanged.
    const ev = nextFeatured(cal);
    const at = ev && ev.at ? Date.parse(ev.at) : NaN;
    const far = label.text !== 'now' && !!clock && Number.isFinite(now) && Number.isFinite(at) && at - now > STRIP_HOT_MS;
    const words = far ? clock : (label.text === 'now' ? 'On now' : (label.text === 'today' ? 'Today' : label.text));
    return { num, words, weather: null, hot: true, soon: false, title };
  }
  const next = nextFeatured(cal);
  const soon = !!(next && today && dayDiff(today, next.day) === 1);
  return { num, words: clock, weather, hot: false, soon, title };
}

function filmTitleFor(ballot, row) {
  if (!ballot || !row || ballot.day !== row.day || ballot.stage !== 'closed') return null;
  const f = Array.isArray(ballot.films) ? ballot.films[Number(ballot.winner)] : null;
  return f && f.title ? `${f.title}${f.year ? ` (${f.year})` : ''}` : null;
}
const rowKey = (r) => `${r.day}|${r.template || r.label}`;

export function eventStrip(cal, opts = {}) {
  try {
    const rows = Array.isArray(cal?.rows) ? cal.rows.filter((r) => r && r.day === cal.today && r.label) : [];
    if (!rows.length) return null;
    const now = Number.isFinite(opts.now) ? opts.now : Date.now();
    const { ballot, outing, screening } = opts;
    // 🎬🍿 09-23 batch 1 (MOVIE_NIGHT_UX_HANDOFF §3.1/§3.3, rulings 2 + 7): with
    // `peek` the doors/playing tap walks you INTO THE PICTURE (go 'screen'); the
    // ▶ on the painted screen is what seats you. `seated` = already on the couch.
    // Without `peek` every word and every `go` is byte-identical to 09-22.
    const peek = !!opts.peek;
    const inCta = opts.seated ? 'Cinema ▶' : 'Go in ▶';
    const running = rows.find((r) => r.phase === 'running');
    // 🚪⏰ 09-23 §3.8 doors at T-30 (ruling 15): the backend lists house.outing
    // while the night's row is still `scheduled` (a DERIVED outing). That is
    // "doors open": the line names the night and counts down to it.
    const on = running || ((outing && outing.id) ? (rows.find((r) => r.featured) || rows[0]) : null);
    if (on) {
      const key = rowKey(on);
      if (screening && screening.state && outing && outing.id) {
        if (screening.state === 'ended') return null;
        const title = screening.title ? `${screening.title}${screening.year ? ` (${screening.year})` : ''}` : on.label;
        const startMs = Date.parse(screening.starts_at);
        if (screening.state === 'seating') {
          const left = Number.isFinite(startMs) ? Math.max(0, Math.round((startMs - now) / 1000)) : 0;
          const clock = left > 0 ? `starts in ${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}` : 'starting';
          return { key, phase: 'doors', emoji: '🎬', text: `Doors open · ${title}`, clock, cta: peek ? inCta : 'Take a seat ▶', go: peek ? 'screen' : 'seat', room: outing.id, hot: true };
        }
        const mins = Number.isFinite(startMs) ? Math.max(0, Math.floor((now - startMs) / 60000)) : 0;
        return { key, phase: 'playing', emoji: '🎬', text: `Now playing · ${title}`, clock: `${mins} min in`, cta: peek ? inCta : 'Join ▶', go: peek ? 'screen' : 'seat', room: outing.id, hot: true };
      }
      if (!running) {
        const ms = Date.parse(on.at) - now;
        const film = filmTitleFor(ballot, on);
        return { key, phase: 'doors', emoji: on.emoji || '📅', text: `Doors open · ${film || on.label}`, clock: Number.isFinite(ms) && ms > 0 ? clockWords(ms) : '', cta: peek ? 'Go in ▶' : 'Go ▶', go: peek ? 'screen' : 'room', room: outing.id, hot: true };
      }
      const away = !!(outing && outing.id);
      // 🌕 09-26: a LOCATED night at home (the moon on the roof, a bake-off in
      // the dining room) gets the same Go into its room's picture. `eventRoom` =
      // the page's invite resolver (roomView.roomInviteFor); absent ⇒ the 📅 as before.
      const here = !away && peek && typeof opts.eventRoom === 'string' && opts.eventRoom ? opts.eventRoom : null;
      return { key, phase: 'running', emoji: on.emoji || '📅', text: `${on.label} is on now`, clock: '', cta: away ? (peek ? 'Go in ▶' : 'Go ▶') : here ? 'Go ▶' : 'Open 📅', go: away ? (peek ? 'screen' : 'room') : here ? 'screen' : 'calendar', room: away ? outing.id : here, hot: true };
    }
    const timed = rows
      .map((r) => ({ r, ms: Date.parse(r.at) - now }))
      .filter((x) => Number.isFinite(x.ms) && x.ms > -STRIP_GRACE_MS)
      .sort((a, b) => a.ms - b.ms)[0];
    if (timed) {
      const { r, ms } = timed;
      const film = filmTitleFor(ballot, r);
      const text = film ? `${film} · ${r.label}` : r.label;
      return { key: rowKey(r), phase: ms > 0 ? 'before' : 'opening', emoji: r.emoji || '📅', text, clock: ms > 0 ? clockWords(ms) : 'doors opening', cta: 'Open 📅', go: 'calendar', room: null, hot: ms <= STRIP_HOT_MS };
    }
    // a timed night past its grace is over (or never fired): it never falls through to a plain line
    const plain = rows.filter((x) => !Number.isFinite(Date.parse(x.at)));
    const r = plain.find((x) => x.featured) || plain[0];
    if (!r) return null;
    return { key: rowKey(r), phase: 'today', emoji: r.emoji || '📅', text: `${r.label} today`, clock: '', cta: 'Open 📅', go: 'calendar', room: null, hot: false };
  } catch (e) { return null; }
}
